/**
 * Status Panel Component
 * Displays bot real-time status summary
 */
import type { BotStatus } from '../../types/botMonitoring'
import { formatCurrency, formatTimestamp } from '../../utils/formatters'

interface StatusPanelProps {
  status: BotStatus
}

export default function StatusPanel({ status }: StatusPanelProps) {
  const statusColor = {
    active: 'text-green-600 bg-green-100',
    stopped: 'text-gray-600 bg-gray-100',
    error: 'text-red-600 bg-red-100',
  }[status.status]

  const statusText = {
    active: '運行中',
    stopped: '已停止',
    error: '錯誤',
  }[status.status]

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Bot 狀態</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
          {statusText}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Wallet Balance */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">錢包餘額</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(status.wallet_balance, status.currency)}
          </div>
        </div>

        {/* Active Offers */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">掛單數</div>
          <div className="text-2xl font-bold text-blue-600">{status.active_offers}</div>
        </div>

        {/* Active Loans */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">貸款數</div>
          <div className="text-2xl font-bold text-green-600">{status.active_loans}</div>
        </div>

        {/* Total Earned */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">總收益</div>
          <div className="text-2xl font-bold text-purple-600">
            {formatCurrency(status.total_earned, status.currency)}
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">已借出總額：</span>
            <span className="ml-2 font-medium text-gray-900">
              {formatCurrency(status.total_lent, status.currency)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">最後更新：</span>
            <span className="ml-2 font-medium text-gray-900">
              {formatTimestamp(status.last_updated)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
